// Annotation Editor Module
// Canvas-based annotation overlay for marking up screenshots

export type AnnotationTool = 'highlight' | 'arrow' | 'freehand' | 'text';

interface Point {
  x: number;
  y: number;
}

interface Annotation {
  type: AnnotationTool;
  color: string;
  strokeWidth: number;
  points: Point[];
  text?: string;
}

export class AnnotationEditor {
  private overlay: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private imageData: string;
  private image: HTMLImageElement | null = null;
  private annotations: Annotation[] = [];
  private currentTool: AnnotationTool = 'highlight';
  private currentColor: string = '#ff0000';
  private strokeWidth: number = 3;
  private isDrawing: boolean = false;
  private currentAnnotation: Annotation | null = null;
  private onComplete: (annotatedImage: string) => void;
  private onCancel: () => void;
  private scale: number = 1;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private pendingTextPoint: Point | null = null;

  constructor(
    imageData: string,
    onComplete: (annotatedImage: string) => void,
    onCancel: () => void
  ) {
    this.imageData = imageData;
    this.onComplete = onComplete;
    this.onCancel = onCancel;
  }

  /**
   * Open the annotation editor
   */
  async open(): Promise<void> {
    // Load the image first
    this.image = await this.loadImage(this.imageData);

    // Create the overlay UI
    this.createOverlay();

    // Set up the canvas
    this.setupCanvas();

    // Set up canvas drawing event listeners (must be after setupCanvas)
    this.setupCanvasEventListeners();

    // Draw the image
    this.render();
  }

  /**
   * Load image from data URL
   */
  private loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  }

  /**
   * Create the fullscreen overlay UI
   */
  private createOverlay(): void {
    this.overlay = document.createElement('div');
    this.overlay.className = 'triagly-annotation-overlay';
    this.overlay.innerHTML = `
      <div class="triagly-annotation-header">
        <div class="triagly-annotation-tools">
          <button type="button" class="triagly-tool-btn active" data-tool="highlight" title="Highlight">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" opacity="0.3" fill="currentColor"/>
              <rect x="3" y="3" width="18" height="18" rx="2"/>
            </svg>
          </button>
          <button type="button" class="triagly-tool-btn" data-tool="arrow" title="Arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="19" x2="19" y2="5"/>
              <polyline points="10,5 19,5 19,14"/>
            </svg>
          </button>
          <button type="button" class="triagly-tool-btn" data-tool="freehand" title="Draw">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 19l7-7 3 3-7 7-3-3z"/>
              <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
              <path d="M2 2l7.586 7.586"/>
            </svg>
          </button>
          <button type="button" class="triagly-tool-btn" data-tool="text" title="Text">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="4 7 4 4 20 4 20 7"/>
              <line x1="9" y1="20" x2="15" y2="20"/>
              <line x1="12" y1="4" x2="12" y2="20"/>
            </svg>
          </button>
          <div class="triagly-tool-divider"></div>
          <input type="color" class="triagly-color-picker" value="#ff0000" title="Color">
        </div>
        <div class="triagly-annotation-actions">
          <button type="button" class="triagly-annotation-btn triagly-btn-clear" title="Clear All">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Clear
          </button>
          <button type="button" class="triagly-annotation-btn triagly-btn-cancel">Cancel</button>
          <button type="button" class="triagly-annotation-btn triagly-btn-done">Done</button>
        </div>
      </div>
      <div class="triagly-annotation-canvas-container">
        <canvas class="triagly-annotation-canvas"></canvas>
      </div>
      <div class="triagly-text-modal" id="triagly-text-modal" style="display: none;">
        <div class="triagly-text-modal-content">
          <h3>Add Text Annotation</h3>
          <input type="text" id="triagly-text-input" placeholder="Enter text..." maxlength="200" autofocus />
          <div class="triagly-text-modal-actions">
            <button type="button" class="triagly-annotation-btn triagly-btn-cancel" id="triagly-text-cancel">Cancel</button>
            <button type="button" class="triagly-annotation-btn triagly-btn-done" id="triagly-text-confirm">Add Text</button>
          </div>
        </div>
      </div>
    `;

    // Inject styles
    this.injectStyles();

    // Add event listeners
    this.setupEventListeners();

    document.body.appendChild(this.overlay);
  }

  /**
   * Set up the canvas with correct dimensions
   */
  private setupCanvas(): void {
    if (!this.overlay || !this.image) return;

    this.canvas = this.overlay.querySelector('.triagly-annotation-canvas') as HTMLCanvasElement;
    const container = this.overlay.querySelector('.triagly-annotation-canvas-container') as HTMLElement;

    if (!this.canvas || !container) return;

    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    // Calculate scale to fit image in container
    const containerRect = container.getBoundingClientRect();
    const maxWidth = containerRect.width - 40; // Padding
    const maxHeight = containerRect.height - 40;

    const imageAspect = this.image.width / this.image.height;
    const containerAspect = maxWidth / maxHeight;

    let canvasWidth: number;
    let canvasHeight: number;

    if (imageAspect > containerAspect) {
      // Image is wider - fit to width
      canvasWidth = Math.min(this.image.width, maxWidth);
      canvasHeight = canvasWidth / imageAspect;
    } else {
      // Image is taller - fit to height
      canvasHeight = Math.min(this.image.height, maxHeight);
      canvasWidth = canvasHeight * imageAspect;
    }

    this.scale = canvasWidth / this.image.width;

    this.canvas.width = canvasWidth;
    this.canvas.height = canvasHeight;
    this.canvas.style.width = `${canvasWidth}px`;
    this.canvas.style.height = `${canvasHeight}px`;

    // Calculate offset for centering
    this.offsetX = (containerRect.width - canvasWidth) / 2;
    this.offsetY = (containerRect.height - canvasHeight) / 2;
  }

  /**
   * Set up UI event listeners (buttons, color picker, etc.)
   */
  private setupEventListeners(): void {
    if (!this.overlay) return;

    // Tool selection
    const toolBtns = this.overlay.querySelectorAll('.triagly-tool-btn');
    toolBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const tool = (e.currentTarget as HTMLElement).dataset.tool as AnnotationTool;
        if (tool) {
          this.currentTool = tool;
          toolBtns.forEach((b) => b.classList.remove('active'));
          (e.currentTarget as HTMLElement).classList.add('active');
        }
      });
    });

    // Color picker
    const colorPicker = this.overlay.querySelector('.triagly-color-picker') as HTMLInputElement;
    if (colorPicker) {
      colorPicker.addEventListener('change', (e) => {
        this.currentColor = (e.target as HTMLInputElement).value;
      });
    }

    // Clear button
    const clearBtn = this.overlay.querySelector('.triagly-btn-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.annotations = [];
        this.render();
      });
    }

    // Cancel button - use class selector to get the header cancel button only
    const headerCancelBtn = this.overlay.querySelector('.triagly-annotation-actions .triagly-btn-cancel');
    if (headerCancelBtn) {
      headerCancelBtn.addEventListener('click', () => this.close(true));
    }

    // Done button
    const doneBtn = this.overlay.querySelector('.triagly-btn-done');
    if (doneBtn) {
      doneBtn.addEventListener('click', () => this.close(false));
    }

    // Text modal event listeners
    const textConfirmBtn = this.overlay.querySelector('#triagly-text-confirm');
    const textCancelBtn = this.overlay.querySelector('#triagly-text-cancel');
    const textInput = this.overlay.querySelector('#triagly-text-input') as HTMLInputElement;

    textConfirmBtn?.addEventListener('click', () => this.confirmTextAnnotation());
    textCancelBtn?.addEventListener('click', () => this.hideTextModal());
    textInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.confirmTextAnnotation();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.hideTextModal();
      }
    });

    // Escape key to cancel
    document.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Set up canvas drawing event listeners
   */
  private setupCanvasEventListeners(): void {
    if (!this.canvas) return;

    // Canvas drawing events
    this.canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.canvas.addEventListener('mouseup', () => this.handleMouseUp());
    this.canvas.addEventListener('mouseleave', () => this.handleMouseUp());

    // Touch support (use passive: false to allow preventDefault)
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      this.handleMouseDown(touch as unknown as MouseEvent);
    }, { passive: false });
    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      this.handleMouseMove(touch as unknown as MouseEvent);
    }, { passive: false });
    this.canvas.addEventListener('touchend', () => this.handleMouseUp());
    this.canvas.addEventListener('touchcancel', () => this.handleMouseUp());
  }

  /**
   * Remove canvas drawing event listeners
   */
  private removeCanvasEventListeners(): void {
    if (!this.canvas) return;

    // Remove all canvas event listeners to prevent memory leaks
    // Note: We can't remove the exact listeners since they're arrow functions,
    // but removing the overlay (which contains the canvas) will clean them up
    this.canvas = null;
    this.ctx = null;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.close(true);
    }
  };

  /**
   * Get canvas coordinates from mouse event
   */
  private getCanvasPoint(e: MouseEvent): Point {
    if (!this.canvas) return { x: 0, y: 0 };

    const rect = this.canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  /**
   * Handle mouse down - start drawing
   */
  private handleMouseDown(e: MouseEvent): void {
    const point = this.getCanvasPoint(e);
    this.isDrawing = true;

    if (this.currentTool === 'text') {
      // For text, show custom modal input
      this.pendingTextPoint = point;
      this.showTextModal();
      this.isDrawing = false;
      return;
    }

    this.currentAnnotation = {
      type: this.currentTool,
      color: this.currentColor,
      strokeWidth: this.strokeWidth,
      points: [point],
    };
  }

  /**
   * Show the text input modal
   */
  private showTextModal(): void {
    if (!this.overlay) return;
    const modal = this.overlay.querySelector('#triagly-text-modal') as HTMLElement;
    const input = this.overlay.querySelector('#triagly-text-input') as HTMLInputElement;
    if (modal && input) {
      modal.style.display = 'flex';
      input.value = '';
      input.focus();
    }
  }

  /**
   * Hide the text input modal
   */
  private hideTextModal(): void {
    if (!this.overlay) return;
    const modal = this.overlay.querySelector('#triagly-text-modal') as HTMLElement;
    if (modal) {
      modal.style.display = 'none';
    }
    this.pendingTextPoint = null;
  }

  /**
   * Confirm text annotation from modal
   */
  private confirmTextAnnotation(): void {
    if (!this.overlay || !this.pendingTextPoint) return;
    const input = this.overlay.querySelector('#triagly-text-input') as HTMLInputElement;
    const text = input?.value.trim();
    if (text) {
      this.annotations.push({
        type: 'text',
        color: this.currentColor,
        strokeWidth: this.strokeWidth,
        points: [this.pendingTextPoint],
        text,
      });
      this.render();
    }
    this.hideTextModal();
  }

  /**
   * Handle mouse move - continue drawing
   */
  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDrawing || !this.currentAnnotation) return;

    const point = this.getCanvasPoint(e);

    if (this.currentTool === 'freehand') {
      // Add point to path
      this.currentAnnotation.points.push(point);
    } else {
      // For highlight and arrow, just update the end point
      if (this.currentAnnotation.points.length === 1) {
        this.currentAnnotation.points.push(point);
      } else {
        this.currentAnnotation.points[1] = point;
      }
    }

    this.render();
  }

  /**
   * Handle mouse up - finish drawing
   */
  private handleMouseUp(): void {
    if (this.isDrawing && this.currentAnnotation && this.currentAnnotation.points.length > 1) {
      this.annotations.push(this.currentAnnotation);
    }
    this.isDrawing = false;
    this.currentAnnotation = null;
    this.render();
  }

  /**
   * Render the canvas with image and annotations
   */
  private render(): void {
    if (!this.ctx || !this.canvas || !this.image) return;

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw the image
    this.ctx.drawImage(this.image, 0, 0, this.canvas.width, this.canvas.height);

    // Draw all annotations
    for (const annotation of this.annotations) {
      this.drawAnnotation(annotation);
    }

    // Draw current annotation being made
    if (this.currentAnnotation) {
      this.drawAnnotation(this.currentAnnotation);
    }
  }

  /**
   * Draw a single annotation
   */
  private drawAnnotation(annotation: Annotation): void {
    if (!this.ctx) return;

    this.ctx.strokeStyle = annotation.color;
    this.ctx.fillStyle = annotation.color;
    this.ctx.lineWidth = annotation.strokeWidth;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    switch (annotation.type) {
      case 'highlight':
        this.drawHighlight(annotation);
        break;
      case 'arrow':
        this.drawArrow(annotation);
        break;
      case 'freehand':
        this.drawFreehand(annotation);
        break;
      case 'text':
        this.drawText(annotation);
        break;
    }
  }

  /**
   * Draw a semi-transparent highlight rectangle
   */
  private drawHighlight(annotation: Annotation): void {
    if (!this.ctx || annotation.points.length < 2) return;

    const [start, end] = annotation.points;
    const width = end.x - start.x;
    const height = end.y - start.y;

    // Semi-transparent fill
    this.ctx.globalAlpha = 0.3;
    this.ctx.fillRect(start.x, start.y, width, height);

    // Solid border
    this.ctx.globalAlpha = 1;
    this.ctx.strokeRect(start.x, start.y, width, height);
  }

  /**
   * Draw an arrow from start to end
   */
  private drawArrow(annotation: Annotation): void {
    if (!this.ctx || annotation.points.length < 2) return;

    const [start, end] = annotation.points;
    const headLength = 15;
    const angle = Math.atan2(end.y - start.y, end.x - start.x);

    // Draw the line
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.stroke();

    // Draw the arrow head
    this.ctx.beginPath();
    this.ctx.moveTo(end.x, end.y);
    this.ctx.lineTo(
      end.x - headLength * Math.cos(angle - Math.PI / 6),
      end.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    this.ctx.moveTo(end.x, end.y);
    this.ctx.lineTo(
      end.x - headLength * Math.cos(angle + Math.PI / 6),
      end.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    this.ctx.stroke();
  }

  /**
   * Draw a freehand path
   */
  private drawFreehand(annotation: Annotation): void {
    if (!this.ctx || annotation.points.length < 2) return;

    this.ctx.beginPath();
    this.ctx.moveTo(annotation.points[0].x, annotation.points[0].y);

    for (let i = 1; i < annotation.points.length; i++) {
      this.ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
    }

    this.ctx.stroke();
  }

  /**
   * Draw text annotation
   */
  private drawText(annotation: Annotation): void {
    if (!this.ctx || !annotation.text || annotation.points.length < 1) return;

    const point = annotation.points[0];

    // Text settings
    this.ctx.font = 'bold 16px sans-serif';
    this.ctx.textBaseline = 'top';

    // Measure text for background
    const metrics = this.ctx.measureText(annotation.text);
    const padding = 4;

    // Draw background
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.fillRect(
      point.x - padding,
      point.y - padding,
      metrics.width + padding * 2,
      20 + padding * 2
    );

    // Draw text
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillText(annotation.text, point.x, point.y);
  }

  /**
   * Close the editor
   */
  private close(cancelled: boolean): void {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleKeyDown);
    this.removeCanvasEventListeners();

    if (cancelled) {
      this.onCancel();
    } else {
      // Generate final annotated image
      const annotatedImage = this.renderFinalImage();
      this.onComplete(annotatedImage);
    }

    // Remove overlay
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  /**
   * Render the final annotated image at full resolution
   */
  private renderFinalImage(): string {
    if (!this.image) return this.imageData;

    // Create a full-resolution canvas
    const canvas = document.createElement('canvas');
    canvas.width = this.image.width;
    canvas.height = this.image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return this.imageData;

    // Draw original image
    ctx.drawImage(this.image, 0, 0);

    // Calculate scale factor
    const scaleFactor = 1 / this.scale;

    // Draw annotations at full resolution
    for (const annotation of this.annotations) {
      ctx.strokeStyle = annotation.color;
      ctx.fillStyle = annotation.color;
      ctx.lineWidth = annotation.strokeWidth * scaleFactor;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Scale points
      const scaledPoints = annotation.points.map((p) => ({
        x: p.x * scaleFactor,
        y: p.y * scaleFactor,
      }));

      const scaledAnnotation = { ...annotation, points: scaledPoints };

      switch (annotation.type) {
        case 'highlight':
          this.drawHighlightOnCtx(ctx, scaledAnnotation);
          break;
        case 'arrow':
          this.drawArrowOnCtx(ctx, scaledAnnotation, scaleFactor);
          break;
        case 'freehand':
          this.drawFreehandOnCtx(ctx, scaledAnnotation);
          break;
        case 'text':
          this.drawTextOnCtx(ctx, scaledAnnotation, scaleFactor);
          break;
      }
    }

    return canvas.toDataURL('image/jpeg', 0.9);
  }

  private drawHighlightOnCtx(ctx: CanvasRenderingContext2D, annotation: Annotation): void {
    if (annotation.points.length < 2) return;
    const [start, end] = annotation.points;
    const width = end.x - start.x;
    const height = end.y - start.y;
    ctx.globalAlpha = 0.3;
    ctx.fillRect(start.x, start.y, width, height);
    ctx.globalAlpha = 1;
    ctx.strokeRect(start.x, start.y, width, height);
  }

  private drawArrowOnCtx(ctx: CanvasRenderingContext2D, annotation: Annotation, scaleFactor: number): void {
    if (annotation.points.length < 2) return;
    const [start, end] = annotation.points;
    const headLength = 15 * scaleFactor;
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(end.x - headLength * Math.cos(angle - Math.PI / 6), end.y - headLength * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(end.x - headLength * Math.cos(angle + Math.PI / 6), end.y - headLength * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  }

  private drawFreehandOnCtx(ctx: CanvasRenderingContext2D, annotation: Annotation): void {
    if (annotation.points.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
    for (let i = 1; i < annotation.points.length; i++) {
      ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
    }
    ctx.stroke();
  }

  private drawTextOnCtx(ctx: CanvasRenderingContext2D, annotation: Annotation, scaleFactor: number): void {
    if (!annotation.text || annotation.points.length < 1) return;
    const point = annotation.points[0];
    const fontSize = 16 * scaleFactor;
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textBaseline = 'top';
    const metrics = ctx.measureText(annotation.text);
    const padding = 4 * scaleFactor;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(point.x - padding, point.y - padding, metrics.width + padding * 2, fontSize * 1.2 + padding * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(annotation.text, point.x, point.y);
  }

  /**
   * Inject annotation editor styles
   */
  private injectStyles(): void {
    const styleId = 'triagly-annotation-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .triagly-annotation-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2000000;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        flex-direction: column;
      }

      .triagly-annotation-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px;
        background: #1a1a1a;
        border-bottom: 1px solid #333;
      }

      .triagly-annotation-tools {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .triagly-tool-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 8px;
        background: #333;
        color: #fff;
        cursor: pointer;
        transition: all 0.2s;
      }

      .triagly-tool-btn:hover {
        background: #444;
      }

      .triagly-tool-btn.active {
        background: #0066cc;
      }

      .triagly-tool-divider {
        width: 1px;
        height: 24px;
        background: #444;
        margin: 0 8px;
      }

      .triagly-color-picker {
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        padding: 0;
        background: none;
      }

      .triagly-color-picker::-webkit-color-swatch-wrapper {
        padding: 4px;
      }

      .triagly-color-picker::-webkit-color-swatch {
        border: none;
        border-radius: 4px;
      }

      .triagly-annotation-actions {
        display: flex;
        gap: 8px;
      }

      .triagly-annotation-btn {
        padding: 8px 16px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .triagly-btn-clear {
        background: transparent;
        color: #999;
        border: 1px solid #444;
      }

      .triagly-btn-clear:hover {
        background: #333;
        color: #fff;
      }

      .triagly-btn-cancel {
        background: #333;
        color: #fff;
      }

      .triagly-btn-cancel:hover {
        background: #444;
      }

      .triagly-btn-done {
        background: #0066cc;
        color: #fff;
      }

      .triagly-btn-done:hover {
        background: #0055aa;
      }

      .triagly-annotation-canvas-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        padding: 20px;
      }

      .triagly-annotation-canvas {
        cursor: crosshair;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
        border-radius: 4px;
      }

      .triagly-text-modal {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000000;
      }

      .triagly-text-modal-content {
        background: #1a1a1a;
        border-radius: 12px;
        padding: 24px;
        min-width: 320px;
        max-width: 400px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      }

      .triagly-text-modal-content h3 {
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 16px 0;
      }

      .triagly-text-modal-content input {
        width: 100%;
        padding: 12px;
        border: 1px solid #444;
        border-radius: 8px;
        background: #2a2a2a;
        color: #fff;
        font-size: 14px;
        outline: none;
        box-sizing: border-box;
      }

      .triagly-text-modal-content input:focus {
        border-color: #0066cc;
      }

      .triagly-text-modal-content input::placeholder {
        color: #666;
      }

      .triagly-text-modal-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        margin-top: 16px;
      }
    `;

    document.head.appendChild(style);
  }
}
